import { Suspense, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Megaphone, Trash2, X } from 'lucide-react'
import { apiClient } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { Button } from '@/shared/ui/button'
import { useConfirm } from '@/shared/hooks/use-confirm'
import { emptyArray } from '@/shared/lib/empty'
import { formatRelativeTime } from '@/shared/lib/formatters'
import { invalidateNotifications } from '@/shared/lib/notificationsInvalidation'
import { todayLocalDate } from '@/shared/lib/localDate'
import { lazyWithRecovery } from '@/lib/lazyWithRecovery'

// The announcement composer's only date field: react-day-picker stays out of the shell's chunk
// until somebody opens the composer (IN-46 Phase 4.4). Picks the export by hand — the module also
// exports DateDayPicker (RB-39 F1), and the loader needs one component type.
const DatePicker = lazyWithRecovery(() => import('@/shared/ui/date-picker').then(m => ({ default: m.DatePicker })))

interface NotificationResponse {
  id: number
  type: string
  message: string
  link: string | null
  read: boolean
  createdAt: string
}

interface GlobalNotificationItem {
  id: number
  message: string
  createdBy: number | null
  expiresAt: string | null
  createdAt: string
}

/**
 * The member's notifications and the studio's announcements: one panel, two hosts. The desktop
 * sidebar's bell drops it open upward; the phone bar's Notifications slot opens it in a bottom
 * sheet (RB-40 D84). Moved out of `AppLayout` (RB-40 phase 2, part B) so both hosts render the
 * same list and the same actions — "Mark all read" (`POST /notifications/read-all`), per-row read
 * on tap (`PATCH /notifications/{id}/read`), "Delete all" (`DELETE /notifications`) and, for an
 * Owner or Admin, "Announce" and the announcement dismiss (`/global-notifications`).
 *
 * The two lists are fetched only while the panel is `open` (`staleTime: 0`, so every opening
 * refetches) — the unread COUNT is the host's, because it drives a badge that shows while the
 * panel is closed.
 *
 * "Delete all" asks through the app's own confirm dialog. It was the one `window.confirm` in the
 * shell: a browser dialog the studio never styled, in English whatever the UI language — and the
 * phone sheet needs a confirm that is ours anyway.
 */
export function NotificationPanel({ open, unreadCount, isOwnerOrAdmin, onNavigate }: {
  /** Fetch the lists only while the host shows the panel. */
  open: boolean
  unreadCount: number
  isOwnerOrAdmin: boolean
  /** Called after a row was tapped, before navigating to its link — the host closes itself. */
  onNavigate: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [confirmDialog, confirm] = useConfirm()
  const [newAnnouncementOpen, setNewAnnouncementOpen] = useState(false)
  const [announcementMessage, setAnnouncementMessage] = useState('')
  const [announcementExpiresAt, setAnnouncementExpiresAt] = useState('')

  const {
    data: notifications = emptyArray<NotificationResponse>(),
    // `isLoading` (TanStack v5: no data yet AND actively fetching) is true only for the
    // panel's very first fetch on open — not on a later reopen, where stale-but-present data
    // is shown while it silently refetches. Without this, the empty state ("No notifications")
    // rendered during that first fetch, indistinguishable from a genuinely empty list, while
    // the badge (a separate, already-resolved query) showed a nonzero count next to it.
    // G2-J3-F7 / G2-J1b-F10 / G2-J6ab-F8.
    isLoading: notificationsLoading,
  } = useQuery<NotificationResponse[]>({
    queryKey: queryKeys.notifications.list,
    queryFn: async () => { const { data } = await apiClient.get('/notifications'); return data },
    enabled: open,
    staleTime: 0,
  })

  const {
    data: globalNotifications = emptyArray<GlobalNotificationItem>(),
    isLoading: globalNotificationsLoading,
  } = useQuery<GlobalNotificationItem[]>({
    queryKey: queryKeys.notifications.global,
    queryFn: async () => { const { data } = await apiClient.get('/global-notifications'); return data },
    enabled: open,
    staleTime: 0,
  })

  // Both feeds share the one empty-vs-loading row at the bottom of the panel.
  const pending = open && (notificationsLoading || globalNotificationsLoading)

  const createAnnouncementMutation = useMutation({
    mutationFn: (payload: { message: string; expiresAt: string | null }) =>
      apiClient.post('/global-notifications', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.notifications.global })
      setNewAnnouncementOpen(false)
      setAnnouncementMessage('')
      setAnnouncementExpiresAt('')
    },
  })

  const deleteAnnouncementMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/global-notifications/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.global }),
  })

  return (
    <>
      {/* Header: title on its own row, actions on a second row that wraps
          gracefully. Previously the title and three actions shared a
          single justify-between row, which collided with longer German
          labels ("Alle als gelesen markieren", "Ankündigen") and made
          buttons overlap each other. Stacking gives each action room. */}
      <div className="flex flex-col gap-1.5 px-3 py-2 border-b">
        <span className="text-sm font-semibold">{t('layout:notifications.title')}</span>
        {(isOwnerOrAdmin || unreadCount > 0 || notifications.length > 0) && (
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
            {isOwnerOrAdmin && (
              <button
                className="flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap touch:min-h-11"
                onClick={() => setNewAnnouncementOpen(v => !v)}
              >
                <Megaphone size={11} /> {t('layout:notifications.announce')}
              </button>
            )}
            {unreadCount > 0 && (
              <button
                className="text-xs text-primary hover:underline whitespace-nowrap touch:min-h-11"
                onClick={async () => {
                  await apiClient.post('/notifications/read-all')
                  invalidateNotifications(qc)
                }}
              >
                {t('layout:notifications.markAllRead')}
              </button>
            )}
            {notifications.length > 0 && (
              <button
                className="flex items-center gap-1 text-xs text-destructive hover:underline whitespace-nowrap ml-auto touch:min-h-11"
                onClick={async () => {
                  const ok = await confirm({
                    title: t('layout:notifications.deleteAllConfirm'),
                    confirmLabel: t('layout:notifications.deleteAll'),
                    variant: 'destructive',
                  })
                  if (!ok) return
                  await apiClient.delete('/notifications')
                  invalidateNotifications(qc)
                }}
              >
                <Trash2 size={11} /> {t('layout:notifications.deleteAll')}
              </button>
            )}
          </div>
        )}
      </div>

      {newAnnouncementOpen && (
        <form
          className="px-3 py-2 border-b bg-slate-50 flex flex-col gap-2"
          onSubmit={async (e) => {
            e.preventDefault()
            await createAnnouncementMutation.mutateAsync({
              message: announcementMessage,
              expiresAt: announcementExpiresAt
                ? new Date(announcementExpiresAt + 'T23:59:59').toISOString()
                : null,
            })
          }}
        >
          <textarea
            className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm resize-none"
            placeholder={t('layout:notifications.announcementPlaceholder')}
            rows={2}
            value={announcementMessage}
            onChange={e => setAnnouncementMessage(e.target.value)}
            required
          />
          <div className="flex items-center gap-2">
            <Suspense fallback={null}>
              <DatePicker
                value={announcementExpiresAt}
                onChange={setAnnouncementExpiresAt}
                placeholder={t('layout:notifications.expiresPlaceholder')}
                className="flex-1 h-7 text-xs"
                minDate={todayLocalDate()}
              />
            </Suspense>
            <Button type="submit" size="sm" className="h-7 text-xs shrink-0" disabled={createAnnouncementMutation.isPending}>
              {createAnnouncementMutation.isPending ? t('layout:notifications.posting') : t('layout:notifications.post')}
            </Button>
            <button type="button" onClick={() => setNewAnnouncementOpen(false)} className="text-xs text-muted-foreground hover:text-foreground shrink-0 touch:min-h-11 touch:min-w-11">
              {t('button.cancel')}
            </button>
          </div>
        </form>
      )}

      {globalNotifications.map(gn => (
        <div key={gn.id} className="px-3 py-2.5 border-b bg-blue-50 flex items-start gap-2">
          <Megaphone size={13} className="shrink-0 mt-0.5 text-blue-600" />
          <div className="flex-1 min-w-0">
            <span className="text-sm leading-snug">{gn.message}</span>
            <div className="text-xs text-muted-foreground mt-0.5">{formatRelativeTime(gn.createdAt)}</div>
          </div>
          {isOwnerOrAdmin && (
            <button
              onClick={() => deleteAnnouncementMutation.mutate(gn.id)}
              className="shrink-0 text-slate-400 hover:text-red-500 touch:flex touch:min-h-11 touch:min-w-11 touch:-my-2.5 touch:-mr-3 touch:items-center touch:justify-center"
              title={t('layout:notifications.dismiss')}
            >
              <X size={12} />
            </button>
          )}
        </div>
      ))}

      <div className="overflow-y-auto flex-1">
        {notifications.length === 0 && globalNotifications.length === 0 && pending && (
          <p className="text-sm text-muted-foreground text-center py-6 flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            {t('layout:suspense.loading')}
          </p>
        )}
        {notifications.length === 0 && globalNotifications.length === 0 && !pending && (
          <p className="text-sm text-muted-foreground text-center py-6">{t('layout:notifications.empty')}</p>
        )}
        {notifications.map(n => (
          <button
            key={n.id}
            className={`w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-accent flex items-start gap-2 ${!n.read ? 'bg-blue-50/60' : ''}`}
            onClick={async () => {
              await apiClient.patch(`/notifications/${n.id}/read`)
              invalidateNotifications(qc)
              onNavigate()
              if (n.link) navigate(n.link.replace(/^https?:\/\/[^/]+/, ''))
            }}
          >
            <span className={`mt-1.5 shrink-0 w-2 h-2 rounded-full ${!n.read ? 'bg-blue-500' : 'bg-transparent'}`} />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm leading-snug line-clamp-2">{n.message}</span>
              <span className="text-xs text-muted-foreground">
                {formatRelativeTime(n.createdAt)}
              </span>
            </div>
          </button>
        ))}
      </div>
      {confirmDialog}
    </>
  )
}
